<template>
    <div v-if="visible">
        <!-- Collapsed rail: the card has no room, so it becomes its icon.
             Same click target, same gating — only the chrome is dropped. -->
        <button
            v-if="collapsed"
            type="button"
            class="w-full text-white hover:text-white hover:bg-gray-800 group flex items-center justify-center rounded-md p-2 disabled:opacity-60 disabled:cursor-not-allowed"
            :disabled="booking"
            title="Need Legal Advice?"
            @click="bookConsult"
        >
            <font-awesome-icon :icon="faScaleBalanced" size="lg" class="shrink-0 text-white" aria-hidden="true" />
            <span class="sr-only">Need Legal Advice?</span>
        </button>

        <div v-else class="bg-white rounded-xl shadow-sm mb-2" :class="expanded ? 'p-4' : 'px-4 py-2.5'">
            <button
                type="button"
                class="flex w-full items-center justify-between gap-2 text-left"
                :aria-expanded="expanded ? 'true' : 'false'"
                aria-controls="legal-advice-card-body"
                data-test="legal-advice-toggle"
                @click="toggleExpanded"
            >
                <span class="flex items-center gap-2">
                    <span class="text-sm font-bold text-gray-900">Need Legal Advice?</span>
                    <span class="menu-badge">NEW</span>
                </span>
                <font-awesome-icon
                    :icon="expanded ? faChevronDown : faChevronUp"
                    class="h-3.5 w-3.5 shrink-0 text-gray-500"
                    aria-hidden="true"
                />
            </button>
            <div v-if="expanded" id="legal-advice-card-body">
                <p class="text-xs text-gray-500 mt-1 leading-relaxed">
                    RiskGuard Pro subscribers get a free 20-minute consultation with a Rocket Lawyer
                    attorney.
                </p>
                <button
                    type="button"
                    class="w-full inline-flex items-center justify-center gap-2 text-sm font-medium text-white bg-rent-risk-blue-400 hover:bg-black rounded-lg px-4 py-2 transition-colors mt-3 disabled:opacity-60 disabled:cursor-not-allowed"
                    :disabled="booking"
                    @click="bookConsult"
                >
                    <font-awesome-icon :icon="faScaleBalanced" class="h-4 w-4" aria-hidden="true" />
                    {{ booking ? 'Opening…' : 'Book a Call' }}
                </button>
                <p v-if="consultError" class="text-xs text-red-600 mt-2">{{ consultError }}</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import axios from 'axios'
import { router, usePage } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronDown, faChevronUp, faScaleBalanced } from '@fortawesome/pro-light-svg-icons'
import { proUpgradeNav } from '@/Layouts/proUpgradeNav'

defineProps({
    // The desktop rail collapses to 64px. The card doesn't fit there, so it
    // renders as a bare icon instead of being hidden — the CTA is the point.
    collapsed: {
        type: Boolean,
        default: false,
    },
})

const page = usePage()

// Landlords only, and only while the RocketLawyer integration is live — the
// endpoint 404s behind the same flag, so showing the card without it would
// offer a button that cannot work.
const visible = computed(() => (
    page.props.auth?.user?.role === 'agent'
    && page.props.rocket_lawyer_lease_enabled === true
))

// Mirrors SubscriptionAccess::checkProtectAccess — an active or trialing
// RiskGuard (base or Pro). The server route is still the real gate; this only
// decides whether to ask for the consult or show the upsell first.
const hasProtectAccess = computed(() => {
    const subscription = page.props.auth?.user?.protect_subscription

    return !! subscription && ['active', 'trialing'].includes(subscription.stripe_sub_status)
})

// 86bc2cgxd: the card starts collapsed to its heading so it leaves room in the
// sidebar; the landlord's choice is remembered per browser. Storage access
// throws in some private-browsing modes, so it is best-effort only.
const EXPANDED_STORAGE_KEY = 'legalAdviceCardExpanded'

const readExpanded = () => {
    try {
        return localStorage.getItem(EXPANDED_STORAGE_KEY) === 'true'
    } catch {
        return false
    }
}

const expanded = ref(readExpanded())

const toggleExpanded = () => {
    expanded.value = ! expanded.value

    try {
        localStorage.setItem(EXPANDED_STORAGE_KEY, String(expanded.value))
    } catch {
        // The toggle still works for this page view.
    }
}

const booking = ref(false)
const consultError = ref(null)

// Provision the agent a RocketLawyer account via Group User Provisioning
// (RAC-10254) and open the returned claim link. router.post returns void in
// Inertia v2, so this uses axios to read the link off the response.
const bookConsult = async () => {
    if (booking.value) {
        return
    }

    // RAC-10257: attorney consults are a paid feature. 86bcd9hg3: this card renders on
    // every page, so rather than its own pop-up it follows the shared Pro rule and sends
    // the landlord where they can buy Pro (the pricing pop-up) or switch (Billing).
    if (! hasProtectAccess.value) {
        router.visit(proUpgradeNav(page.props.auth?.user, route)?.href ?? route('pricing', { upgrade: 1 }))

        return
    }

    booking.value = true
    consultError.value = null

    try {
        const { data } = await axios.post(route('legal-consult'))

        if (data?.link) {
            window.open(data.link, '_blank', 'noopener')
        }
    } catch (error) {
        consultError.value = error.response?.data?.message
            || 'Could not start your consult right now. Please try again.'
    } finally {
        booking.value = false
    }
}
</script>
